import { useEffect, useRef, useState } from "react";

import { Loader2 } from "lucide-react";

import { trackEvent } from "../../analytics/analytics";
import { useIsNeedlBranding } from "../../branding/hooks";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "../../components/ui/toaster";
import { useFetchUserInfo } from "../../hooks/useFetchUserInfo";
import { getRequestOrigin } from "../../utils/requestOrigin";
import {
  useFetchMemorySettings,
  useUpdateMemorySettings,
  useUpdateUserName,
} from "../hooks";

import { AppearanceSection } from "./AppearanceSection";

const MAX_INSTRUCTION_LENGTH = 5000;
const SHOW_APPEARANCE_SECTION = false;

const UserAvatar = ({ name }: { name: string }) => {
  const initials = name
    .split(" ")
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="w-14 h-14 rounded-full flex items-center justify-center font-semibold text-base bg-primary bg-[image:var(--primary-gradient-avatar,none)] text-primary-foreground shrink-0 select-none ring-4 ring-primary/10">
      {initials}
    </div>
  );
};

export const GeneralSettings = () => {
  const { data: userInfo } = useFetchUserInfo();
  const { data: memorySettings } = useFetchMemorySettings();
  const isNeedl = useIsNeedlBranding();
  const updateMemorySettings = useUpdateMemorySettings();
  const updateUserName = useUpdateUserName();

  const [fullName, setFullName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [instruction, setInstruction] = useState("");
  const [initialFullName, setInitialFullName] = useState("");
  const [initialDisplayName, setInitialDisplayName] = useState("");
  const [initialInstruction, setInitialInstruction] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const expandTextarea = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      const newHeight = Math.min(textarea.scrollHeight, 320);
      textarea.style.height = `${newHeight}px`;
    }
  };

  useEffect(() => {
    if (userInfo) {
      const username = userInfo.username ?? "";
      setFullName(username);
      setInitialFullName(username);
    }
  }, [userInfo]);

  useEffect(() => {
    if (memorySettings) {
      const displayNameValue = memorySettings.display_name ?? "";
      const instructionValue = memorySettings.instruction ?? "";
      setDisplayName(displayNameValue);
      setInstruction(instructionValue);
      setInitialDisplayName(displayNameValue);
      setInitialInstruction(instructionValue);
    }
  }, [memorySettings]);

  useEffect(() => {
    expandTextarea();
  }, [instruction]);

  const handleSave = async () => {
    const [memoryResult, nameResult] = await Promise.allSettled([
      updateMemorySettings.mutateAsync({
        display_name: displayName,
        instruction,
      }),
      fullName !== (userInfo?.username ?? "")
        ? updateUserName.mutateAsync(fullName)
        : Promise.resolve(),
    ]);

    if (memoryResult.status === "rejected")
      toast({
        title: "Failed to save AI preferences",
        variant: "destructive",
      });
    if (nameResult.status === "rejected")
      toast({
        title: "Failed to update name",
        variant: "destructive",
      });
    if (
      memoryResult.status === "fulfilled" &&
      nameResult.status === "fulfilled"
    ) {
      toast({ title: "Settings saved" });

      const fieldsChanged: string[] = [];
      if (fullName !== initialFullName) fieldsChanged.push("full_name");
      if (displayName !== initialDisplayName)
        fieldsChanged.push("display_name");
      if (instruction !== initialInstruction)
        fieldsChanged.push("ai_preferences");

      trackEvent("profile_updated", {
        fields_changed: fieldsChanged,
        user_id: userInfo?.public_uuid,
        source: getRequestOrigin(),
      });
    }
  };

  const isSaving = updateMemorySettings.isPending || updateUserName.isPending;

  const hasChanges =
    fullName !== initialFullName ||
    displayName !== initialDisplayName ||
    instruction !== initialInstruction;

  return (
    <div className="py-8 space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">General</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage your profile and personal preferences.
        </p>
      </div>

      <div className="rounded-xl p-6 space-y-8">
        <div className="flex items-center gap-5">
          <UserAvatar name={fullName || displayName || "User"} />
          <div className="flex-1 grid grid-cols-2 gap-4">
            <div>
              <Label
                htmlFor="fullName"
                className="text-xs text-muted-foreground"
              >
                Full Name
              </Label>
              <Input
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                maxLength={100}
                placeholder="Your full name"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label
                htmlFor="nickname"
                className="text-xs text-muted-foreground"
              >
                Display Name
              </Label>
              <Input
                id="nickname"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                maxLength={100}
                placeholder="How would you like to be called?"
                className="mt-1.5"
              />
            </div>
          </div>
        </div>

        <div>
          <p className="text-sm font-medium">AI Preferences</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Personal preferences AskNeedl will consider in responses, within
            AskNeedl&apos;s guidelines.
          </p>
          <Textarea
            ref={textareaRef}
            id="preferences"
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            maxLength={MAX_INSTRUCTION_LENGTH}
            placeholder="Be concise and analytical. Lead with the key insight..."
            className="mt-3 min-h-32 resize-none overflow-y-auto transition-all duration-300"
          />
          <p className="mt-1.5 text-right text-xs text-muted-foreground">
            {instruction.length} / {MAX_INSTRUCTION_LENGTH}
          </p>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            className="min-w-24"
          >
            {isSaving && (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            )}
            Save changes
          </Button>
        </div>
      </div>

      {SHOW_APPEARANCE_SECTION && (
        <>
          <div className="border-t mt-2" />
          {isNeedl && <AppearanceSection />}
        </>
      )}
    </div>
  );
};
